import React from 'react';
import { useTranslation } from 'react-i18next';
import AboutCampaignAndCharityDocument from './AboutCampaignAndCharitySection.gql';
import useFullPageQuery from '../../hooks/useFullPageQuery';
import { AboutCampaignAndCharityQuery } from '../../../types/__generated__';
import AboutCampaign from '../AboutCampaign/AboutCampaign';
import AboutCharity from '../AboutCharity/AboutCharity';
import { filterByTypename } from '../../utils/graphql';

type AboutCampaignAndCharityProps = {
  withExternalUrl?: boolean;
  beneficiariesLimit?: any;
};

const AboutCampaignAndCharitySection: React.FunctionComponent<AboutCampaignAndCharityProps> = props => {
  const { withExternalUrl, beneficiariesLimit = 6 } = props;
  const { t } = useTranslation('common', { useSuspense: false });
  const options = {
    variables: {
      withExternalUrl: withExternalUrl,
      beneficiariesLimit: beneficiariesLimit,
    },
  };
  const result = useFullPageQuery<AboutCampaignAndCharityQuery>(
    AboutCampaignAndCharityDocument,
    options,
  );

  if (result.error) {
    return null;
  }

  if (!result.data?.page?.relationships) {
    return null;
  }

  const {
    relationships: {
      beneficiaries: {
        nodes: beneficiaryNodes,
        totalCount: beneficiariesTotalCount,
      },
      campaigns: { nodes: campaigns, totalCount: campaignTotalCount },
    },
  } = result.data.page;

  const charities = filterByTypename(beneficiaryNodes, 'Charity');

  const [{ logo: charityLogo }] = charities;

  const showCampaign = campaigns.length > 0 && charities.length === 1;

  return (
    <div className="container jg-space-pvmd">
      <div className="row" data-qa="about-charity-campaign" id="aboutSection">
        {showCampaign && (
          <section className="jg-pages-teams-about col-xl-6 jg-space-plmd jg-space-pln@xl">
            <header>
              <h1 className="jg-h5 jg-space-mbmd">
                {t('page.aboutTheCampaign', {
                  count: campaignTotalCount,
                })}
              </h1>
            </header>
            <AboutCampaign
              campaigns={campaigns.map(x => ({
                logo: x.logo || charityLogo,
                title: x.title,
                url: x.url,
                summary: x.summary,
                id: x.id,
                charities: charities.map(c => ({
                  name: c.name || '',
                  url: c.profilePageUrl,
                })),
              }))}
            />
          </section>
        )}
        <section
          className={`jg-pages-teams-about jg-space-plmd jg-space-phn@xl ${
            showCampaign ? ' col-xl-6 jg-space-plsm' : ''
          }`}
        >
          <header>
            <h1 className="jg-h5 jg-space-mbmd">
              {t('page.aboutTheCharity', { count: beneficiariesTotalCount })}
            </h1>
          </header>
          <AboutCharity
            charities={charities.map(x => ({
              id: x.id,
              url: x.externalUrl,
              logo: x.logo,
              name: x.name,
              profilePageUrl: x.profilePageUrl,
              description: x.description,
              registrationNumber: x.registrationNumber,
            }))}
          />
        </section>
      </div>
    </div>
  );
};

AboutCampaignAndCharitySection.defaultProps = {
  withExternalUrl: false,
};

export default AboutCampaignAndCharitySection;
